/* Gegevens van de offerte: nummer, klant en contactpersoon, project, referentie,
 * PDF-sjabloon, prijsstelling en labels. Titel, datums en status staan in de kop. */

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { Quote, QuoteTemplate } from '@/types/quote';

type Bewaar = (w: Partial<Quote>, o?: { silent?: boolean; refetch?: boolean }) => Promise<void>;
const stil = { silent: true, refetch: false } as const;

const json = <T,>(v: unknown): T | null => (Array.isArray(v) ? (v as T) : v ? (JSON.parse(v as string) as T) : null);

function Veld({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={htmlFor} className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

export function GegevensBlok({ quote, onUpdate, onTekstenVervangen }: {
  quote: Quote;
  onUpdate: Bewaar;
  /** Het sjabloon heeft de teksten vervangen: het tekstblok moet opnieuw beginnen. */
  onTekstenVervangen?: () => void;
}) {
  const [organisaties, setOrganisaties] = useState<{ id: string; name: string }[]>([]);
  const [contacten, setContacten] = useState<{ id: string; first_name: string; last_name: string }[]>([]);
  const [projecten, setProjecten] = useState<{ id: string; name: string; project_number: string }[]>([]);
  const [sjablonen, setSjablonen] = useState<{ id: string; name: string }[]>([]);
  const [nummer, setNummer] = useState(quote.quote_number);
  const [referentie, setReferentie] = useState((quote as any).reference ?? '');
  const [label, setLabel] = useState('');
  const [sjabloonVraag, setSjabloonVraag] = useState<QuoteTemplate | null>(null);

  useEffect(() => {
    setNummer(quote.quote_number);
    setReferentie((quote as any).reference ?? '');
  }, [quote.id, quote.quote_number]);

  useEffect(() => {
    Promise.all([
      supabase.from('organizations').select('id, name').is('deleted_at', null).order('name'),
      supabase.from('projects').select('id, name, project_number').is('deleted_at', null).order('project_number', { ascending: false }),
      supabase.from('quote_templates').select('id, name').is('deleted_at', null).order('name'),
    ]).then(([o, p, t]) => {
      setOrganisaties(o.data ?? []);
      setProjecten((p.data as any) ?? []);
      setSjablonen(t.data ?? []);
    });
  }, []);

  useEffect(() => {
    if (!quote.organization_id) return setContacten([]);
    supabase.from('contacts').select('id, first_name, last_name').eq('org_id', quote.organization_id).order('first_name')
      .then(({ data }) => setContacten(data ?? []));
  }, [quote.organization_id]);

  const kiesSjabloon = async (id: string) => {
    if (id === 'none') {
      await onUpdate({ template_id: null }, stil);
      return;
    }
    const { data } = await supabase.from('quote_templates').select('*').eq('id', id).single();
    if (!data) return;
    const t: QuoteTemplate = {
      ...(data as any),
      placeholder_positions: json(data.placeholder_positions),
      default_payment_conditions: json(data.default_payment_conditions),
      page_backgrounds: json(data.page_backgrounds),
      page_count: data.page_count || 1,
    };
    const heeftTeksten = t.default_project_goal || t.default_work_description || t.default_advice_conclusion || t.default_disclaimer
      || (t.default_payment_conditions?.length ?? 0) > 0;
    if (heeftTeksten) setSjabloonVraag(t);
    else await onUpdate({ template_id: id }, stil);
  };

  const pasSjabloonToe = async (metTeksten: boolean) => {
    const t = sjabloonVraag;
    if (!t) return;
    const w: Partial<Quote> = { template_id: t.id };
    if (metTeksten) {
      if (t.default_project_goal) w.project_goal = t.default_project_goal;
      if (t.default_work_description) w.work_description = t.default_work_description;
      if (t.default_advice_conclusion) w.advice_conclusion = t.default_advice_conclusion;
      if (t.default_disclaimer) w.disclaimer = t.default_disclaimer;
      if (t.default_payment_conditions) w.payment_conditions = t.default_payment_conditions;
    }
    await onUpdate(w, { silent: true, refetch: true });
    if (metTeksten) onTekstenVervangen?.();
    setSjabloonVraag(null);
    toast.success(metTeksten ? 'Sjabloon en standaardteksten toegepast' : 'Sjabloon gekoppeld');
  };

  const voegLabelToe = () => {
    const l = label.trim();
    if (l && !quote.labels.includes(l)) onUpdate({ labels: [...quote.labels, l] }, stil);
    setLabel('');
  };

  const chargingTraject = quote.charging_location_id || quote.charging_opportunity_id;

  /** Het eerstvolgende vrije nummer binnen het project (P28 → P28-3), zoals het CRM het altijd koos. */
  const volgendNummer = async () => {
    if (!quote.project_id || !quote.project?.project_number) return;
    const { data, error } = await supabase.rpc('get_next_quote_suffix', {
      p_project_id: quote.project_id,
      p_base_number: quote.project.project_number,
    });
    if (error) return toast.error('Volgend nummer bepalen mislukt', { description: error.message });
    if (data && data !== quote.quote_number) await onUpdate({ quote_number: data }, { silent: true, refetch: true });
  };

  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-3 rounded-xl border bg-background px-4 py-3 sm:grid-cols-2">
      <Veld label="Offertenummer" htmlFor="gegevens-nummer">
        <div className="flex gap-2">
          <Input
            id="gegevens-nummer"
            value={nummer}
            onChange={(e) => setNummer(e.target.value)}
            onBlur={() => {
              const n = nummer.trim();
              if (n && n !== quote.quote_number) onUpdate({ quote_number: n }, stil).catch(() => setNummer(quote.quote_number));
              else setNummer(quote.quote_number);
            }}
            className="h-8 font-mono"
          />
          {quote.project?.project_number && (
            <Button type="button" variant="outline" size="sm" className="h-8 shrink-0" onClick={volgendNummer} title="Eerstvolgende vrije nummer binnen het project">
              Auto
            </Button>
          )}
        </div>
      </Veld>
      <Veld label="Uw referentie / kenmerk" htmlFor="gegevens-referentie">
        <Input
          id="gegevens-referentie"
          value={referentie}
          placeholder="Bijvoorbeeld REF-2026-001"
          onChange={(e) => setReferentie(e.target.value)}
          onBlur={() => referentie !== ((quote as any).reference ?? '') && onUpdate({ reference: referentie || null } as any, stil)}
          className="h-8"
        />
      </Veld>
      <Veld label="Organisatie">
        <Select value={quote.organization_id ?? ''} onValueChange={(v) => onUpdate({ organization_id: v, contact_id: null }, stil)}>
          <SelectTrigger className="h-8"><SelectValue placeholder="Kies een organisatie" /></SelectTrigger>
          <SelectContent>{organisaties.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
      </Veld>
      <Veld label="Contactpersoon">
        <Select value={quote.contact_id ?? ''} onValueChange={(v) => onUpdate({ contact_id: v }, { silent: true, refetch: true })} disabled={contacten.length === 0}>
          <SelectTrigger className="h-8"><SelectValue placeholder={contacten.length ? 'Kies een contactpersoon' : 'Geen contacten bij deze organisatie'} /></SelectTrigger>
          <SelectContent>{contacten.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
        </Select>
      </Veld>
      <Veld label="Project">
        {chargingTraject ? (
          <span className="flex h-8 items-center text-sm text-muted-foreground">Hoort bij {quote.charging_location_id ? 'een Kavel' : 'een Opportunity'}; geen project.</span>
        ) : (
          <Select value={quote.project_id ?? ''} onValueChange={(v) => onUpdate({ project_id: v }, { silent: true, refetch: true })}>
            <SelectTrigger className="h-8"><SelectValue placeholder="Geen project" /></SelectTrigger>
            <SelectContent>{projecten.map((p) => <SelectItem key={p.id} value={p.id}>{p.project_number} · {p.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </Veld>
      <Veld label="PDF-sjabloon">
        <Select value={quote.template_id ?? 'none'} onValueChange={kiesSjabloon}>
          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Geen sjabloon (standaard)</SelectItem>
            {sjablonen.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Veld>
      <Veld label="Prijzen">
        <RadioGroup
          value={quote.pricing_mode || 'inclusive'}
          onValueChange={(v: 'inclusive' | 'exclusive') => onUpdate({ pricing_mode: v }, stil)}
          className="flex h-8 items-center gap-4"
        >
          <label className="flex cursor-pointer items-center gap-1.5 text-sm"><RadioGroupItem value="inclusive" />incl. btw</label>
          <label className="flex cursor-pointer items-center gap-1.5 text-sm"><RadioGroupItem value="exclusive" />excl. btw</label>
        </RadioGroup>
      </Veld>
      <Veld label="Labels" htmlFor="gegevens-label">
        <div className="flex flex-wrap items-center gap-1.5">
          {quote.labels.map((l) => (
            <Badge key={l} variant="secondary" className="gap-1">
              {l}
              <button type="button" aria-label={`Label ${l} weghalen`} onClick={() => onUpdate({ labels: quote.labels.filter((x) => x !== l) }, stil)} className="hover:text-destructive">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Input
            id="gegevens-label"
            value={label}
            placeholder="Label toevoegen"
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                voegLabelToe();
              }
            }}
            onBlur={() => label.trim() && voegLabelToe()}
            className="h-7 w-36 text-xs"
          />
        </div>
      </Veld>

      <Dialog open={sjabloonVraag !== null} onOpenChange={(o) => !o && setSjabloonVraag(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Standaardteksten overnemen?</DialogTitle>
            <DialogDescription>Dit sjabloon heeft standaardteksten. Die vervangen wat nu in de offerte staat.</DialogDescription>
          </DialogHeader>
          <ul className="flex flex-col gap-1 text-sm">
            {sjabloonVraag?.default_project_goal && <li>✓ Projectdoel</li>}
            {sjabloonVraag?.default_work_description && <li>✓ Werkomschrijving</li>}
            {sjabloonVraag?.default_advice_conclusion && <li>✓ Advies en conclusie</li>}
            {sjabloonVraag?.default_disclaimer && <li>✓ Disclaimer</li>}
            {(sjabloonVraag?.default_payment_conditions?.length ?? 0) > 0 && <li>✓ Betalingstermijnen ({sjabloonVraag!.default_payment_conditions!.length})</li>}
          </ul>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => pasSjabloonToe(false)}>Alleen sjabloon</Button>
            <Button onClick={() => pasSjabloonToe(true)}>Alles overnemen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
